import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { cacheData, getCachedData } from "../lib/offlineSync";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Folder, ListChecks, Truck, ClipboardText, Warning, CheckCircle, Clock, CurrencyDollar,
  ShieldCheck, ShieldWarning, TrendUp, ArrowRight, Timer, ArrowsClockwise, Pulse,
  GearSix, Eye, EyeSlash, ArrowsDownUp, CaretUp, CaretDown,
  Lightning, User, PencilSimple, Trash, Plus, Link as LinkIcon, ChartBar,
  CalendarBlank, CaretLeft, CaretRight as CaretRightIcon, Wrench, Briefcase, MapPin, Star,
  EnvelopeSimple, Receipt, Hand, UsersThree
} from "@phosphor-icons/react";
import GetDirectionsButton from "../components/GetDirectionsButton";
import MarkAsArrivedButton from "../components/MarkAsArrivedButton";
import EnRouteButton from "../components/EnRouteButton";
import SuggestedReassignments from "../components/SuggestedReassignments";
import ClientWaitingCard from "../components/ClientWaitingCard";
import RamsReadinessCard from "../components/hs/RamsReadinessCard";
import HseDashboardTile from "../components/hse/HseDashboardTile";
import { emergencySla } from "../lib/emergencySla";
import { SlaLadderBeacon, SlaTierBadge, SlaTimer } from "../components/EmergencySla";
import PendingStartDatesWidget from "../components/PendingStartDatesWidget";
import PendingPaymentVerifications from "../components/PendingPaymentVerifications";
import SLAResponseWidget from "../components/SLAResponseWidget";
import BreachInsightsWidget from "../components/BreachInsightsWidget";
import { toast } from "sonner";
import { computeRowBars, rowGridTemplateRows, BAR_HEIGHT_PX } from "../lib/calendarLayout";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import OnboardingWidget from "../components/OnboardingWidget";
import WelcomeTour from "../components/WelcomeTour";
import TomorrowJobsAtRiskWidget from "../components/TomorrowJobsAtRiskWidget";
import UnassignedThisWeekWidget from "../components/UnassignedThisWeekWidget";
import AssigneeChip from "../components/AssigneeChip";
import ComplianceBadge from "../components/ComplianceBadge";
import useComplianceBadges from "../hooks/useComplianceBadges";
import QuoteSlaAlertPopup from "../components/QuoteSlaAlertPopup";
import TeamPulseWidget from "../components/TeamPulseWidget";
import { fmtDate as ukDate } from "../lib/dateUtil";

const CHART_COLORS = ["#1D4ED8", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

function StatCard({ icon: Icon, label, value, color, delay, href, accent }) {
  const content = (
    <div className={`border border-border rounded-md p-4 bg-card animate-fade-in ${delay} ${accent || ""} ${href ? "card-lift hover:border-primary/40 cursor-pointer group" : ""}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">{label}</span>
        <Icon size={18} className={`${color} ${href ? "group-hover:scale-110 transition-transform" : ""}`} weight="duotone" />
      </div>
      <p className="text-2xl font-bold font-[Chivo] tracking-tight">{value}</p>
    </div>
  );
  return href ? <Link to={href}>{content}</Link> : content;
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium status-${status}`}>
      {status?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
}

function HealthBadge({ health }) {
  const cfg = {
    on_track: { label: "On Track", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" },
    at_risk: { label: "At Risk", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
    blocked: { label: "Blocked", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
    completed: { label: "Completed", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400" },
  };
  const c = cfg[health] || cfg.on_track;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${c.cls}`}>{c.label}</span>;
}

function SchedulerWidget() {
  const [scheduler, setScheduler] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/scheduler/status").then(({ data }) => setScheduler(data.compliance_check)),
      API.get("/scheduler/history?limit=5").then(({ data }) => setHistory(data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data } = await API.post("/scheduler/run-now");
      toast.success(data.message);
      // Refresh status
      const [statusRes, histRes] = await Promise.all([
        API.get("/scheduler/status"),
        API.get("/scheduler/history?limit=5")
      ]);
      setScheduler(statusRes.data.compliance_check);
      setHistory(histRes.data);
    } catch { toast.error("Failed to run compliance check"); }
    finally { setRunning(false); }
  };

  if (loading) return null;
  if (!scheduler) return null;

  const healthCfg = {
    healthy: { label: "Healthy", cls: "text-emerald-600 dark:text-emerald-400", pulse: "bg-emerald-500" },
    overdue: { label: "Overdue", cls: "text-amber-600 dark:text-amber-400", pulse: "bg-amber-500" },
    stalled: { label: "Stalled", cls: "text-red-600 dark:text-red-400", pulse: "bg-red-500" },
    no_data: { label: "No Data", cls: "text-slate-500", pulse: "bg-slate-400" },
  };
  const h = healthCfg[scheduler.health] || healthCfg.no_data;

  const lastRun = scheduler.last_run;
  const lastRunTime = lastRun?.ran_at ? new Date(lastRun.ran_at).toLocaleString() : "Never";

  return (
    <div className="border border-border rounded-md animate-fade-in" data-testid="scheduler-widget">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Timer size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold font-[Chivo]">Compliance Scheduler</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5">
            <span className={`relative flex h-2.5 w-2.5`}>
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${h.pulse}`} />
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${h.pulse}`} />
            </span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider ${h.cls}`}>{h.label}</span>
          </span>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-bold font-[Chivo]">{scheduler.total_runs}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Runs</p>
          </div>
          <div>
            <p className="text-lg font-bold font-[Chivo]">{scheduler.total_reminders}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Reminders</p>
          </div>
          <div>
            <p className="text-lg font-bold font-[Chivo]">{lastRun?.docs_scanned ?? 0}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Docs Scanned</p>
          </div>
        </div>

        {/* Last run info */}
        <div className="text-xs text-muted-foreground flex items-center justify-between">
          <span>Last run: <span className="font-medium text-foreground">{lastRunTime}</span></span>
          {lastRun?.status && (
            <Badge variant={lastRun.status === "success" ? "default" : "destructive"} className="text-[9px] h-4">
              {lastRun.status}
            </Badge>
          )}
        </div>

        {/* Recent history */}
        {history.length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Recent Runs</p>
            {history.slice(0, 5).map((run, i) => (
              <div key={run.id || i} className="flex items-center justify-between text-[11px] py-1 border-b border-border/50 last:border-0" data-testid={`scheduler-run-${i}`}>
                <span className="text-muted-foreground">{new Date(run.ran_at).toLocaleString()}</span>
                <div className="flex items-center gap-2">
                  <span className="text-foreground font-medium">{run.count} sent</span>
                  <span className={`h-1.5 w-1.5 rounded-full ${run.status === "success" ? "bg-emerald-500" : "bg-red-500"}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Run now button */}
        <Button size="sm" variant="outline" className="w-full" onClick={handleRunNow} disabled={running} data-testid="scheduler-run-now-btn">
          <ArrowsClockwise size={14} className={`mr-1 ${running ? "animate-spin" : ""}`} />
          {running ? "Running..." : "Run Compliance Check Now"}
        </Button>
      </div>
    </div>
  );
}

function DashboardCalendar() {
  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emergencyJobs, setEmergencyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    Promise.all([
      API.get("/jobs?limit=200").then(({ data }) => setJobs(data.jobs || [])).catch(() => {}),
      API.get("/tasks?limit=200").then(({ data }) => setTasks(data.tasks || data || [])).catch(() => {}),
      API.get("/emergency-jobs?limit=200").then(({ data }) => setEmergencyJobs(Array.isArray(data) ? data : (data.emergency_jobs || data.jobs || []))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Compliance ack summary for any job rendered in the dashboard mini-cal.
  const complianceByJob = useComplianceBadges(jobs.map(j => j.id));

  const today = new Date();
  const getWeekStart = (offset) => {
    const d = new Date(today);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offset * 7);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const weekStart = getWeekStart(weekOffset);
  // Phase 269 — show 2 weeks (14 days) at a glance instead of 1
  const weekDates = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const todayStr = fmtDate(today);
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Map items by date — multi-day items occupy every cell from start→end within
  // the visible window. Each occupancy records its bar position (single | start |
  // middle | end) so the render can show connected bar segments.
  const itemsByDate = {};
  weekDates.forEach(d => { itemsByDate[fmtDate(d)] = []; });

  const windowStart = fmtDate(weekDates[0]);
  const windowEnd = fmtDate(weekDates[weekDates.length - 1]);

  // Flat event list for the continuous-bar layout helper.
  const barItems = [];

  const pushSpan = (rawStart, rawEnd, common) => {
    const start = rawStart && rawStart >= windowStart ? rawStart : windowStart;
    const end = rawEnd && rawEnd <= windowEnd ? rawEnd : windowEnd;
    let cur = new Date(start + "T00:00:00");
    const stopDate = new Date(end + "T00:00:00");
    if (cur > stopDate) return;
    const continuesLeft = rawStart && rawStart < windowStart;
    const continuesRight = rawEnd && rawEnd > windowEnd;
    // Track per-day occupancy so we can still show the "+N more" footer.
    while (cur <= stopDate) {
      const ds = fmtDate(cur);
      if (itemsByDate[ds] !== undefined) {
        const isFirst = ds === start;
        const isLast = ds === end;
        let position;
        if (isFirst && isLast) position = "single";
        else if (isFirst) position = "start";
        else if (isLast) position = "end";
        else position = "middle";
        itemsByDate[ds].push({ ...common, position, continuesLeft: isFirst && continuesLeft, continuesRight: isLast && continuesRight });
      }
      cur.setDate(cur.getDate() + 1);
    }
    // Push a single flat event for bar-overlay rendering.
    if (rawStart && rawEnd) {
      barItems.push({
        id: `${common.type}-${common.id}`,
        startISO: rawStart,
        endISO: rawEnd,
        startTime: common.time || "",
        item: common,
      });
    }
  };

  emergencyJobs.forEach(e => {
    const sd = e.schedule?.start_date || (e.created_at ? e.created_at.split("T")[0] : null);
    if (!sd) return;
    const ed = e.schedule?.end_date || sd;
    const assigned = e.assigned_vendor_name || e.assigned_operative_name || "";
    const assignedKind = e.assigned_vendor_name ? "supplier" : e.assigned_operative_name ? "operative" : "";
    pushSpan(sd, ed, { type: "emergency", id: e.id, title: e.title, time: e.schedule?.start_time, status: e.status, ref: e.reference, startDate: sd, endDate: ed, assigned, assignedKind, href: `/emergency-jobs/${e.id}` });
  });

  jobs.forEach(j => {
    const sd = j.schedule?.start_date;
    if (!sd) return;
    const ed = j.schedule?.end_date || sd;
    const assigned = j.assigned_vendor_name || j.assigned_operative_name || "";
    const assignedKind = j.assigned_vendor_name ? "supplier" : j.assigned_operative_name ? "operative" : "";
    pushSpan(sd, ed, { type: "job", id: j.id, title: j.title, time: j.schedule?.start_time, endTime: j.schedule?.end_time, startDate: sd, endDate: ed, status: j.status, assigned, assignedKind, ref: j.reference, href: `/jobs/${j.id}` });
  });

  tasks.forEach(t => {
    const primaryStart = t.start_date || t.due_date?.split("T")[0];
    const primaryEnd = t.end_date || primaryStart;
    const assigned = t.assigned_to_name || t.vendor_name || "";
    const assignedKind = t.assigned_type === "vendor" ? "supplier" : t.assigned_type === "in_house" ? "operative" : (t.vendor_name ? "supplier" : (t.assigned_to_name ? "operative" : ""));
    if (primaryStart) {
      pushSpan(primaryStart, primaryEnd, { type: "task", id: t.id, title: t.title, time: t.start_time, status: t.status, startDate: primaryStart, endDate: primaryEnd, assigned, assignedKind, project: t.project_name, href: t.project_id ? `/projects/${t.project_id}` : "/projects" });
    }
    (t.scheduled_dates || []).forEach((s, sIdx) => {
      const sd = s.start_date;
      if (!sd) return;
      const ed = s.end_date || sd;
      pushSpan(sd, ed, { type: "task", id: `${t.id}__s${sIdx + 1}`, title: t.title, time: s.start_time, status: t.status, startDate: sd, endDate: ed, assigned, assignedKind, project: t.project_name, href: t.project_id ? `/projects/${t.project_id}` : "/projects" });
    });
  });

  const weekTitle = `${weekDates[0].toLocaleDateString("en-GB", { day: "numeric", month: "short" })} — ${weekDates[13].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  // Count UNIQUE items per type (a 3-day job is 1 job, not 3; a 2-session task is 1 task not 2)
  const seenIds = { job: new Set(), emergency: new Set(), task: new Set() };
  Object.values(itemsByDate).forEach(arr => arr.forEach(it => {
    if (seenIds[it.type]) {
      const rootId = String(it.id).split("__s")[0];
      seenIds[it.type].add(rootId);
    }
  }));
  const jobCount = seenIds.job.size;
  const emergencyCount = seenIds.emergency.size;
  const taskCount = seenIds.task.size;

  // Entity tint (light background by type) — used on the calendar pill
  const TYPE_TINT = {
    emergency: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    job: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    task: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300",
  };

  // Status dot color — dark blue scheduled / orange in-progress / green done / RED overdue.
  // For multi-day jobs we compare against the schedule's end_date (or due_date for tasks),
  // so a job in flight on day 2 of a 3-day window is NOT flagged red.
  const isCompletedStatus = (s) => ["completed", "approved", "completed_by_vendor", "resolved", "closed"].includes(s);
  const isInProgressStatus = (s) => ["in_progress", "awaiting_materials", "awaiting_review", "acknowledged"].includes(s);
  const getDotColor = (item) => {
    const s = item.status;
    if (isCompletedStatus(s)) return "#10b981"; // emerald (completed)
    // Overdue = item's effective end date is strictly before today AND not completed
    const effectiveEnd = item.endDate;
    if (effectiveEnd && effectiveEnd < todayStr && !isCompletedStatus(s)) return "#dc2626"; // red
    if (isInProgressStatus(s)) return "#ea580c"; // orange
    // Default = "scheduled" / open / pending
    return "#1d4ed8"; // dark blue
  };

  return (
    <div className="border border-border rounded-md animate-fade-in" data-testid="dashboard-calendar">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} className="text-primary" weight="fill" />
          <h3 className="text-sm font-semibold font-[Chivo] section-header-line pb-3 mb-4">Schedule Overview</h3>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground mr-2">
            <span className="flex items-center gap-1" data-testid="dash-cal-count-emergency"><Lightning size={10} className="text-red-500" weight="fill" /> {emergencyCount}</span>
            <span className="flex items-center gap-1" data-testid="dash-cal-count-jobs"><Briefcase size={10} className="text-blue-500" /> {jobCount} job{jobCount !== 1 ? "s" : ""}</span>
            <span className="flex items-center gap-1" data-testid="dash-cal-count-tasks"><ListChecks size={10} className="text-violet-500" /> {taskCount} task{taskCount !== 1 ? "s" : ""}</span>
          </div>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setWeekOffset(w => w - 2)} data-testid="dash-cal-prev"><CaretLeft size={12} /></Button>
          <Button variant="ghost" size="sm" className="text-[10px] h-6 px-2" onClick={() => setWeekOffset(0)} data-testid="dash-cal-today">Today</Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setWeekOffset(w => w + 2)} data-testid="dash-cal-next"><CaretRightIcon size={12} /></Button>
        </div>
      </div>

      {/* Week title */}
      <div className="px-4 py-1.5 border-b border-border bg-muted/30">
        <p className="text-xs font-medium text-center">{weekTitle}</p>
      </div>

      {loading ? (
        <div className="px-4 py-8 text-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div>
      ) : (
        <div data-testid="dash-cal-grid">
          {/* Render 2 weekly rows (7 days each). Each row uses CSS grid so
             multi-day bars span columns and stay on the same vertical slot. */}
          {[0, 1].map((rowIdx) => {
            const rowDays = weekDates.slice(rowIdx * 7, rowIdx * 7 + 7).map(d => fmtDate(d));
            const { bars, maxSlot } = computeRowBars(rowDays, barItems);
            const MAX_VISIBLE = 8;
            const visible = bars.filter(b => b.slot < MAX_VISIBLE);
            const hidden = new Map(rowDays.map(d => [d, 0]));
            bars.forEach(b => {
              if (b.slot < MAX_VISIBLE) return;
              for (let c = b.startCol - 1; c < b.startCol - 1 + b.span; c++) {
                hidden.set(rowDays[c], (hidden.get(rowDays[c]) || 0) + 1);
              }
            });
            const slotsForGrid = Math.max(1, Math.min(MAX_VISIBLE, maxSlot + 1));
            return (
              <div key={rowIdx} className={rowIdx === 1 ? "border-t border-border" : ""}>
                {/* Day header row */}
                <div className="grid grid-cols-7 divide-x divide-border">
                  {rowDays.map((ds, i) => {
                    const d = new Date(ds + "T00:00:00");
                    const isToday = ds === todayStr;
                    return (
                      <div key={ds} className={`text-center py-1.5 border-b border-border ${isToday ? "bg-primary/10" : "bg-muted/30"}`}>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{DAYS[i]}</p>
                        <p className={`text-sm font-bold mt-0.5 ${isToday ? "bg-primary text-primary-foreground rounded-full h-6 w-6 flex items-center justify-center mx-auto" : ""}`}>
                          {d.getDate()}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {/* Day cells + bar overlay row */}
                <div
                  className="grid grid-cols-7 divide-x divide-border relative"
                  style={{ gridTemplateRows: rowGridTemplateRows(slotsForGrid, 0) }}
                >
                  {rowDays.map((ds, i) => {
                    const isToday = ds === todayStr;
                    const hiddenCount = hidden.get(ds) || 0;
                    return (
                      <div
                        key={ds}
                        style={{ gridColumn: `${i + 1}`, gridRow: `1 / -1` }}
                        className={`min-h-[230px] relative ${isToday ? "bg-primary/[0.04]" : ""}`}
                        data-testid={`dash-cal-day-${ds}`}
                      >
                        {hiddenCount > 0 && (
                          <p className="absolute bottom-1 left-1.5 text-[10px] text-muted-foreground font-medium pointer-events-none">+{hiddenCount} more</p>
                        )}
                      </div>
                    );
                  })}
                  {visible.map(b => {
                    const item = b.event.item;
                    const tint = TYPE_TINT[item.type] || TYPE_TINT.job;
                    const dotColor = getDotColor(item);
                    return (
                      <Link
                        key={`${item.type}-${item.id}-r${rowIdx}`}
                        to={item.href}
                        className={`flex items-center gap-1 px-1.5 text-[10px] leading-tight font-medium hover:brightness-95 transition-all rounded ${tint}`}
                        style={{
                          gridColumn: `${b.startCol} / span ${b.span}`,
                          gridRow: 2 + b.slot,
                          height: `${BAR_HEIGHT_PX - 2}px`,
                          marginLeft: b.continuesLeft ? 0 : 3,
                          marginRight: b.continuesRight ? 0 : 3,
                          marginTop: 1,
                          alignSelf: "start",
                          position: "relative",
                          zIndex: 5,
                        }}
                        title={`${item.type === "emergency" ? "Emergency · " : item.type === "task" ? "Task · " : "Job · "}${item.title}${item.startDate !== item.endDate ? ` (${item.startDate} → ${item.endDate})` : ""}${item.assigned ? ` · 👤 ${item.assigned}` : item.type === "task" ? "" : " · Unassigned"}${item.status ? " · " + item.status.replace(/_/g, " ") : ""}`}
                        data-testid={`dash-cal-item-${item.id}${b.isMultiDay ? "-bar" : ""}`}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} data-testid={`dash-cal-dot-${item.id}`} />
                        {b.continuesLeft && <span className="opacity-60 shrink-0">‹</span>}
                        {item.time && <span className="opacity-70 shrink-0">{item.time}</span>}
                        <span className="truncate flex-1">{item.title}</span>
                        {item.type === "job" && <ComplianceBadge summary={complianceByJob[item.id]} jobId={item.id} size={10} />}
                        <AssigneeChip
                          name={item.assigned}
                          kind={item.assignedKind}
                          type={item.type}
                          title={item.title}
                          dateRange={item.startDate === item.endDate ? item.startDate : `${item.startDate} → ${item.endDate}`}
                          status={item.status}
                          testId={`dash-cal-assignee-${item.id}`}
                        />
                        {b.continuesRight && <span className="opacity-60 shrink-0">›</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="px-4 py-2 border-t border-border flex items-center justify-center gap-x-4 gap-y-1 flex-wrap text-[10px] text-muted-foreground" data-testid="dash-cal-legend">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm bg-red-100 border border-red-200 dark:bg-red-900/40 dark:border-red-800" /> Emergency</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm bg-blue-100 border border-blue-200 dark:bg-blue-900/40 dark:border-blue-800" /> Job</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm bg-violet-100 border border-violet-200 dark:bg-violet-900/40 dark:border-violet-800" /> Task</span>
        <span className="opacity-50">·</span>
        <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#1d4ed8" }} /> Scheduled</span>
        <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#ea580c" }} /> In progress</span>
        <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#10b981" }} /> Completed</span>
        <span className="flex items-center gap-1"><span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#dc2626" }} /> Overdue</span>
      </div>

      {/* Footer link */}
      <div className="px-4 py-2 border-t border-border text-center">
        <Link to="/jobs" className="text-[10px] text-primary hover:underline font-medium" data-testid="dash-cal-view-all">View full calendar</Link>
      </div>
    </div>
  );
}


function StakeholderDashboard({ stats }) {
  const [portfolio, setPortfolio] = useState(null);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [loadingPortfolio, setLoadingPortfolio] = useState(true);

  useEffect(() => {
    API.get("/stakeholder/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {}).finally(() => setLoadingPortfolio(false));
    API.get("/compliance/summary").then(({ data }) => setComplianceSummary(data)).catch(() => {});
  }, []);

  if (loadingPortfolio) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const summary = portfolio?.summary || {};
  const projects = portfolio?.projects || [];

  return (
    <div data-testid="stakeholder-dashboard">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Folder} label="Total Projects" value={summary.total_projects || 0} color="text-blue-500" delay="animate-delay-1" href="/projects" />
        <StatCard icon={TrendUp} label="On Track" value={summary.on_track || 0} color="text-emerald-500" delay="animate-delay-2" href="/projects" />
        <StatCard icon={Warning} label="At Risk" value={summary.at_risk || 0} color="text-amber-500" delay="animate-delay-3" href="/projects" />
        <StatCard icon={CurrencyDollar} label="Total Budget" value={`£${(summary.total_budget || 0).toLocaleString()}`} color="text-green-500" delay="animate-delay-4" href="/reports" />
      </div>

      {/* Health Overview Bar */}
      <div className="border border-border rounded-md p-4 mb-6 animate-fade-in" data-testid="health-overview">
        <h3 className="text-sm font-semibold font-[Chivo] mb-3">Portfolio Health</h3>
        <div className="flex gap-2 h-4 rounded-full overflow-hidden bg-muted">
          {summary.on_track > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${(summary.on_track / (summary.total_projects || 1)) * 100}%` }} title={`On Track: ${summary.on_track}`} />}
          {summary.at_risk > 0 && <div className="bg-amber-500 transition-all" style={{ width: `${(summary.at_risk / (summary.total_projects || 1)) * 100}%` }} title={`At Risk: ${summary.at_risk}`} />}
          {summary.blocked > 0 && <div className="bg-red-500 transition-all" style={{ width: `${(summary.blocked / (summary.total_projects || 1)) * 100}%` }} title={`Blocked: ${summary.blocked}`} />}
          {summary.completed > 0 && <div className="bg-blue-500 transition-all" style={{ width: `${(summary.completed / (summary.total_projects || 1)) * 100}%` }} title={`Completed: ${summary.completed}`} />}
        </div>
        <div className="flex gap-4 mt-2 text-[10px]">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> On Track ({summary.on_track || 0})</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> At Risk ({summary.at_risk || 0})</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500" /> Blocked ({summary.blocked || 0})</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> Completed ({summary.completed || 0})</span>
        </div>
      </div>

      {/* Compliance Alert */}
      {complianceSummary && (complianceSummary.expired > 0 || complianceSummary.expiring_soon > 0 || complianceSummary.missing_files > 0) && (
        <div className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-md p-4 mb-6 animate-fade-in" data-testid="compliance-alert">
          <div className="flex items-center gap-2 mb-1">
            <ShieldWarning size={18} className="text-amber-600 dark:text-amber-400" />
            <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Compliance Alerts</h3>
          </div>
          {(complianceSummary.expired > 0 || complianceSummary.expiring_soon > 0) && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {complianceSummary.expired > 0 && <span className="font-medium">{complianceSummary.expired} expired</span>}
              {complianceSummary.expired > 0 && complianceSummary.expiring_soon > 0 && " and "}
              {complianceSummary.expiring_soon > 0 && <span className="font-medium">{complianceSummary.expiring_soon} expiring soon</span>}
              {" supplier compliance document(s) require attention."}
            </p>
          )}
          {complianceSummary.missing_files > 0 && (
            <div className="mt-1 flex items-start justify-between gap-3 flex-wrap">
              <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="compliance-missing-files-text">
                <span className="font-medium">{complianceSummary.missing_files}</span>{" legacy compliance record(s) have no file attached — "}
                <Link to="/vendors" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-200" data-testid="compliance-missing-files-link">re-upload now</Link>.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7"
                data-testid="email-missing-file-suppliers-btn"
                onClick={async () => {
                  try {
                    const { data } = await API.post("/compliance/email-missing-file-suppliers");
                    toast.success(data.message || `Emailed ${data.sent} supplier(s).`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed to email suppliers.");
                  }
                }}
              >
                <EnvelopeSimple size={14} className="mr-1" /> Email Suppliers to Upload
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Project Cards */}
      <h3 className="text-base font-semibold font-[Chivo] mb-4 section-header-line">Project Portfolio</h3>
      {projects.length === 0 ? (
        <div className="empty-state" data-testid="no-projects">
          <Folder size={32} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No projects in portfolio yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Create your first project to get started</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map(p => (
            <Link key={p.id} to={`/projects/${p.id}`} className="block border border-border rounded-md p-4 card-lift hover:border-primary/40 group" data-testid={`portfolio-project-${p.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">{p.name}</p>
                  <p className="text-[10px] text-muted-foreground">{p.reference} | {p.property_address || "No address"}</p>
                </div>
                <HealthBadge health={p.health} />
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Progress value={p.progress} className="h-1.5 flex-1" />
                <span className="text-[10px] font-semibold text-muted-foreground shrink-0">{p.progress}%</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[10px] text-muted-foreground">
                <div><span className="block font-medium text-foreground">{p.task_count}</span>Tasks</div>
                <div><span className="block font-medium text-foreground">{p.completed_count}</span>Done</div>
                <div><span className={`block font-medium ${p.overdue_count > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"}`}>{p.overdue_count}</span>Overdue</div>
              </div>
              {p.budget > 0 && (
                <div className="mt-2 pt-2 border-t border-border flex justify-between text-[10px]">
                  <span className="text-muted-foreground">Budget: <span className="font-medium text-foreground">£{p.budget.toLocaleString()}</span></span>
                  <span className="text-muted-foreground">Target: <span className="font-medium text-foreground">{p.target_date || "-"}</span></span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalyticsWidgets() {
  const [analytics, setAnalytics] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get("/analytics/overview").then(({ data }) => setAnalytics(data)).catch(() => {}),
      API.get("/dashboard/kpi-charts").then(({ data }) => setKpi(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading || (!analytics && !kpi)) return null;

  const pc = analytics?.project_completion || {};
  const pieData = [
    { name: "Completed", value: pc.completed || 0 },
    { name: "In Progress", value: pc.in_progress || 0 },
    { name: "Planned", value: pc.planned || 0 },
    { name: "On Hold", value: pc.on_hold || 0 },
  ].filter(d => d.value > 0);

  const spendData = analytics?.monthly_spend || [];
  const jobPipeline = kpi?.job_pipeline || [];
  const spendTrend = kpi?.spend_trend || [];
  const topSuppliers = kpi?.top_suppliers || [];
  const reqConversion = kpi?.request_conversion || {};
  const taskDist = kpi?.task_distribution || [];
  const budgetVsActual = kpi?.budget_vs_actual || {};

  const PIPELINE_COLORS = { scheduled: "#3b82f6", in_progress: "#f59e0b", completed: "#10b981", cancelled: "#94a3b8" };
  const TASK_DIST_COLORS = ["#3b82f6", "#0ea5e9", "#f59e0b", "#14b8a6", "#10b981", "#ef4444"];

  return (
    <div className="mt-6 space-y-6" data-testid="analytics-widgets">
      <h2 className="text-base font-semibold font-[Chivo] section-header-line">Analytics & KPIs</h2>

      {/* Row 1: Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="border border-border rounded-md p-4 bg-card card-lift" data-testid="widget-completion-rate">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Completion Rate</h3>
          <p className="text-3xl font-bold font-[Chivo]">{pc.rate || 0}%</p>
          <p className="text-[10px] text-muted-foreground">{pc.completed}/{pc.total} projects</p>
        </div>
        <div className="border border-border rounded-md p-4 bg-card card-lift" data-testid="widget-turnaround">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Avg Turnaround</h3>
          <p className="text-3xl font-bold font-[Chivo]">{analytics?.avg_task_turnaround_days || 0}<span className="text-sm font-normal text-muted-foreground ml-1">days</span></p>
          <p className="text-[10px] text-muted-foreground">Creation to completion</p>
        </div>
        <div className="border border-border rounded-md p-4 bg-card card-lift" data-testid="widget-conversion-rate">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Request Conversion</h3>
          <p className="text-3xl font-bold font-[Chivo] text-emerald-600">{reqConversion.rate || 0}%</p>
          <p className="text-[10px] text-muted-foreground">{reqConversion.converted}/{reqConversion.total} converted</p>
        </div>
        <div className="border border-border rounded-md p-4 bg-card card-lift" data-testid="widget-budget-variance">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Budget vs Actual</h3>
          <p className="text-3xl font-bold font-[Chivo]">£{((budgetVsActual.actual || 0) / 1000).toFixed(0)}k</p>
          <p className="text-[10px] text-muted-foreground">of £{((budgetVsActual.budget || 0) / 1000).toFixed(0)}k budget</p>
        </div>
      </div>

      {/* Row 2: Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Job Pipeline Funnel */}
        {jobPipeline.length > 0 && (
          <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-job-pipeline">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Job Pipeline</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={jobPipeline} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={90} />
                <Tooltip formatter={v => `${v} jobs`} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {jobPipeline.map((entry, i) => <Cell key={i} fill={PIPELINE_COLORS[entry.status] || CHART_COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Spend Trend */}
        {spendTrend.length > 0 && (
          <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-spend-trend">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Spend Trend (6 Months)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={spendTrend}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `£${Number(v).toLocaleString()}`} />
                <Bar dataKey="spend" fill="#1D4ED8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Row 3: Distribution Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        {pieData.length > 0 && (
          <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-project-breakdown">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Project Breakdown</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} label={({ name, value }) => `${name}: ${value}`}>
                  {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {taskDist.length > 0 && (
          <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-task-distribution">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Task Status Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={taskDist} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} label={({ label, count }) => `${label}: ${count}`}>
                  {taskDist.map((_, i) => <Cell key={i} fill={TASK_DIST_COLORS[i % TASK_DIST_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Row 4: Supplier Performance & Monthly Spend */}
      <div className="grid md:grid-cols-2 gap-4">
        {topSuppliers.length > 0 && (
          <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-supplier-performance">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Top Rated Suppliers</h3>
            <div className="space-y-2.5">
              {topSuppliers.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3">
                  <span className="text-xs font-bold w-5 text-muted-foreground">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{s.company_name}</p>
                    <p className="text-[10px] text-muted-foreground">{s.trade || "General"}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Star size={12} weight="fill" className="text-amber-400" />
                    <span className="text-xs font-bold">{s.average_rating}</span>
                    <span className="text-[10px] text-muted-foreground">({s.rating_count})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {spendData.length > 0 && (
          <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-monthly-spend">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Monthly Project Spend</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={spendData}>
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `£${v.toLocaleString()}`} />
                <Tooltip formatter={v => `£${v.toLocaleString()}`} />
                <Bar dataKey="spend" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {(analytics?.top_vendors || []).length > 0 && (
        <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-top-vendors">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Top Suppliers by Completed Tasks</h3>
          <div className="space-y-2">
            {analytics.top_vendors.slice(0, 5).map((v, i) => (
              <div key={v.vendor_id} className="flex items-center gap-3">
                <span className="text-xs font-bold w-5 text-muted-foreground">{i + 1}</span>
                <div className="flex-1">
                  <p className="text-xs font-medium">{v.company_name}</p>
                  <p className="text-[10px] text-muted-foreground">{v.trade}</p>
                </div>
                <Badge variant="outline" className="text-[10px]">{v.completed_tasks} tasks</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmailVerificationBanner() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const handleResend = async () => {
    setSending(true);
    try {
      await API.post("/auth/resend-verification");
      setSent(true);
      toast.success("Verification email sent");
    } catch {
      toast.error("Failed to send verification email");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mb-4 px-3 py-2.5 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 flex items-center justify-between gap-2" data-testid="email-verification-banner">
      <div className="flex items-center gap-2">
        <EnvelopeSimple size={16} className="text-blue-600 shrink-0" weight="fill" />
        <span className="text-xs font-medium text-blue-800 dark:text-blue-300">Please verify your email address to unlock all features.</span>
      </div>
      {sent ? (
        <span className="text-[10px] text-blue-600 font-medium shrink-0">Email sent!</span>
      ) : (
        <Button variant="ghost" size="sm" className="text-xs text-blue-700 hover:text-blue-900 shrink-0 h-6" onClick={handleResend} disabled={sending} data-testid="resend-verification-btn">
          {sending ? "Sending..." : "Resend Email"}
        </Button>
      )}
    </div>
  );
}

function InHouseLiveTeamWidget() {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/operatives/live-locations");
      setOps(data.live || []);
    } catch { /* non-fatal */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-in-house-live">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <MapPin size={16} className="text-emerald-600" /> Live Crews
        </h3>
        <Link to="/live-team-map" className="text-xs text-primary hover:underline">Open map →</Link>
      </div>
      {loading && !ops.length ? (
        <p className="text-xs text-muted-foreground py-4 text-center">Loading…</p>
      ) : ops.length === 0 ? (
        <p className="text-xs text-muted-foreground py-6 text-center">No-one's on site right now.</p>
      ) : (
        <>
          <p className="text-2xl font-bold mb-2">{ops.length} <span className="text-xs font-normal text-muted-foreground uppercase tracking-wide">on site</span></p>
          <ul className="space-y-1.5 max-h-[180px] overflow-y-auto">
            {ops.slice(0, 5).map((o) => (
              <li key={`${o.entity_id}-${o.operative_id}`} className="flex items-center gap-2 text-xs">
                <span className={`h-2 w-2 rounded-full ${o.is_emergency ? "bg-red-500" : o.team === "supplier" ? "bg-green-500" : "bg-blue-500"} shrink-0`} title={o.team === "supplier" ? o.supplier_name : "In-house"} />
                <span className="font-medium truncate flex-1">{o.operative_name}{o.team === "supplier" && <span className="text-muted-foreground font-normal"> · {o.supplier_name}</span>}</span>
                <Link to={`/${o.entity_type === "emergency_job" ? "emergency-jobs" : "jobs"}/${o.entity_id}`} className="text-muted-foreground hover:text-primary truncate max-w-[40%]">
                  {o.title}
                </Link>
                <span className="text-muted-foreground shrink-0">{o.on_site_minutes != null ? `${o.on_site_minutes}m` : ""}</span>
              </li>
            ))}
          </ul>
          {ops.length > 5 && <p className="text-[10px] text-muted-foreground mt-2 text-center">+{ops.length - 5} more</p>}
        </>
      )}
    </div>
  );
}

function InHouseExpensesPendingWidget() {
  const [data, setData] = useState({ expenses: [], totals_by_status: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/expenses?status=submitted&limit=5");
      setData(data);
    } catch { /* non-fatal */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const t = data.totals_by_status?.submitted || { amount: 0, count: 0 };

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-in-house-expenses">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Receipt size={16} className="text-amber-600" /> Expenses Awaiting Approval
        </h3>
        <Link to="/settings/expenses" className="text-xs text-primary hover:underline">Review all →</Link>
      </div>
      {loading && !data.expenses.length ? (
        <p className="text-xs text-muted-foreground py-4 text-center">Loading…</p>
      ) : t.count === 0 ? (
        <p className="text-xs text-muted-foreground py-6 text-center">Nothing in the queue. Nice.</p>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mb-3">
            <p className="text-2xl font-bold">£{Number(t.amount).toFixed(2)}</p>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t.count} pending</p>
          </div>
          <ul className="space-y-1.5 max-h-[180px] overflow-y-auto">
            {data.expenses.slice(0, 5).map((x) => (
              <li key={x.id} className="flex items-center gap-2 text-xs">
                <span className="font-semibold shrink-0 w-14">£{Number(x.amount).toFixed(2)}</span>
                <span className="truncate flex-1">{x.operative_name} · <span className="text-muted-foreground capitalize">{x.category}</span></span>
                <Link to={`/${x.entity_type === "emergency_job" ? "emergency-jobs" : "jobs"}/${x.entity_id}`} className="text-muted-foreground hover:text-primary truncate max-w-[35%]">
                  {x.job_title || "Job"}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function InHousePosPendingWidget() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/in-house/pos");
      setPos(data.pos || []);
    } catch { /* non-fatal */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const drafts = pos.filter(p => p.status === "draft");
  const issued = pos.filter(p => p.status === "issued");
  const draftTotal = drafts.reduce((acc, p) => acc + Number(p.total || 0), 0);

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid="widget-in-house-pos">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Receipt size={16} className="text-amber-600" /> In-House Purchase Orders
        </h3>
        <Link to="/in-house/pos" className="text-xs text-primary hover:underline" data-testid="widget-pos-view-all">View all →</Link>
      </div>

      {loading && !pos.length ? (
        <p className="text-xs text-muted-foreground py-4 text-center">Loading…</p>
      ) : pos.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">No purchase orders yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Link
            to="/in-house/pos?status=draft"
            className="block group rounded-md -m-1 p-1 hover:bg-muted/40 transition-colors"
            data-testid="widget-pos-drafts-tile"
          >
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Drafts</p>
            <p className="text-2xl font-bold leading-tight group-hover:text-primary transition-colors">{drafts.length}</p>
            <p className="text-[10px] text-muted-foreground">£{draftTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
          </Link>
          <Link
            to="/in-house/pos?status=issued"
            className="block group rounded-md -m-1 p-1 hover:bg-muted/40 transition-colors"
            data-testid="widget-pos-issued-tile"
          >
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Awaiting Ack.</p>
            <p className="text-2xl font-bold leading-tight text-blue-600 dark:text-blue-400 group-hover:underline">{issued.length}</p>
            <p className="text-[10px] text-muted-foreground">issued, not acknowledged</p>
          </Link>
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
        <Link
          to="/in-house/pos?bulk=1"
          className="flex-1 text-center text-xs px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors font-medium"
          data-testid="widget-pos-bulk-generate"
        >
          Generate POs (this week)
        </Link>
        <Link
          to="/in-house/pos"
          className="text-xs px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors font-medium"
          data-testid="widget-pos-new"
        >
          New PO
        </Link>
      </div>
    </div>
  );
}





export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [wlName, setWlName] = useState(null);
  const [wlItems, setWlItems] = useState(null);

  const openWorkload = (name) => {
    setWlName(name);
    setWlItems(null);
    API.get(`/dashboard/workload-items?name=${encodeURIComponent(name)}`)
      .then(({ data }) => setWlItems(data)).catch(() => setWlItems({ requests: [], jobs: [] }));
  };
  const [loading, setLoading] = useState(true);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const [savingWidgets, setSavingWidgets] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Real-time pulse triggers — key maps to widget (emergencies | jobs | tasks | work_orders)
  const [pulseKey, setPulseKey] = useState(null);
  const [pulseNonce, setPulseNonce] = useState(0);

  // Listen for notifications broadcast by the Layout WebSocket. Refresh stats + pulse.
  useEffect(() => {
    if (user?.role !== "vendor") return;
    const handler = (evt) => {
      const n = evt?.detail || {};
      const t = n.type || "";
      let target = null;
      if (t === "emergency_job" || t === "emergency_acknowledged") target = "emergencies";
      else if (t === "job_assigned") target = "jobs";
      else if (t === "task_assigned") target = "tasks";
      else if (t === "work_order_issued") target = "work_orders";
      if (!target) return;
      setPulseKey(target);
      setPulseNonce(n => n + 1);
      // Refresh stats so counters move live
      fetchDashboard();
      // Clear pulse after animation completes (~3.2s to match 1.6s × 2 iterations)
      setTimeout(() => setPulseKey(cur => (cur === target ? null : cur)), 3300);
    };
    window.addEventListener("necos:notification", handler);
    return () => window.removeEventListener("necos:notification", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    const goOnline = () => { setIsOffline(false); fetchDashboard(); };
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  const fetchDashboard = () => {
    API.get("/dashboard/stats")
      .then(({ data }) => { setStats(data); cacheData('dashboard_stats', data); })
      .catch(async () => {
        const cached = await getCachedData('dashboard_stats');
        if (cached) setStats(cached);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboard();
    if (["super_admin", "admin", "project_manager"].includes(user?.role)) {
      API.get("/compliance/summary").then(({ data }) => setComplianceSummary(data)).catch(() => {});
      API.get("/dashboard/widgets").then(({ data }) => setWidgets(data.widgets || [])).catch(() => {});
      API.get("/dashboard/workload").then(({ data }) => setWorkload(data)).catch(() => {});
    }
  }, [user?.role]);

  const toggleWidget = (wId) => {
    setWidgets(prev => prev.map(w => w.id === wId ? { ...w, visible: !w.visible } : w));
  };

  const moveWidget = (idx, dir) => {
    setWidgets(prev => {
      const arr = [...prev];
      const targetIdx = idx + dir;
      if (targetIdx < 0 || targetIdx >= arr.length) return arr;
      [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
      return arr.map((w, i) => ({ ...w, order: i }));
    });
  };

  const saveWidgets = async () => {
    setSavingWidgets(true);
    try {
      await API.put("/dashboard/widgets", { widgets });
      toast.success("Dashboard layout saved");
      setShowWidgetConfig(false);
    } catch { toast.error("Failed to save"); }
    finally { setSavingWidgets(false); }
  };

  const resetWidgets = async () => {
    try {
      const { data } = await API.post("/dashboard/widgets/reset");
      setWidgets(data.widgets || []);
      toast.success("Dashboard reset to defaults");
      setShowWidgetConfig(false);
    } catch { toast.error("Failed to reset"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const isVendor = user?.role === "vendor";
  const isStakeholder = user?.role === "stakeholder";

  if (isStakeholder) {
    return (
      <div data-testid="dashboard-page">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Portfolio Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Welcome back, {user?.name}</p>
        </div>
        <StakeholderDashboard stats={stats} />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Missed Quote SLA acknowledgment popup — every admin must tick to close */}
      {isAdmin && <QuoteSlaAlertPopup />}
      {/* Offline Banner */}
      {isOffline && (
        <div className="mb-4 px-3 py-2 rounded-md bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 flex items-center gap-2" data-testid="offline-banner">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-xs font-medium text-amber-800 dark:text-amber-300">Offline — showing cached data. Changes will sync when you're back online.</span>
        </div>
      )}

      {/* Email Verification Banner */}
      {user?.email_verified === false && (
        <EmailVerificationBanner />
      )}

      {/* Onboarding Checklist */}
      {isAdmin && <OnboardingWidget />}
      {isAdmin && <TeamPulseWidget />}
      {isAdmin && <PendingPaymentVerifications />}

      {/* Phase 231i — first-visit welcome tour for admins (auto-opens once, dismissable) */}
      {isAdmin && (
        <WelcomeTour
          storageKey={`necos_admin_tour_seen_${user?._id || user?.id || "u"}`}
          steps={[
            { title: "Welcome 👋", body: "This is your dashboard — the daily heartbeat of your property operation. Here's a quick 60-second tour of the bits that save admins hours every week." },
            { title: "Quick Create (`C`)", body: "Press `C` anywhere in the app to spin up a Job, Project, Task or Quote in seconds. Power users — try Gmail-style `G` then `J/P/T/D` for instant navigation." },
            { title: "Job Requests → Quotes → Jobs", body: "Capture a request, invite suppliers to quote, recommend one to your client, then convert to a tracked Job — all from one place. The Start-date Kanban above flags any negotiation needing your nudge." },
            { title: "Suggested Reassignments", body: "We watch every supplier's SLA grade in the background and gently suggest swapping out slow ones for proven alternatives in the same area + trade." },
            { title: "AI Assistant + Insights", body: "Tap the AI button (bottom right) for natural-language answers about your operation. Try: 'show me overdue tasks' or 'find me a plumber near SW1A'." },
            { title: "You're set 🚀", body: "Need help? Hit `?` for keyboard shortcuts, or visit Settings → Help. Re-watch this tour any time from your profile menu." },
          ]}
        />
      )}

      {/* Phase 253 / 258 — SLA + Breach widgets relocated to render after Stats and before Supplier Compliance (inside the dynamic loop) */}

      {/* Suggested reassignments — flag jobs on slow suppliers with a better alt */}
      {isAdmin && <div className="mb-6"><ClientWaitingCard /></div>}
      {isAdmin && <div className="mb-6"><HseDashboardTile /></div>}
      {isAdmin && <div className="mb-6"><RamsReadinessCard compact /></div>}
      {isAdmin && <div className="mb-6"><SuggestedReassignments limit={10} compact /></div>}

      {/* Start-date negotiations Kanban — surface in-flight admin↔supplier start-date rounds */}
      {isAdmin && <div className="mb-6"><PendingStartDatesWidget /></div>}

      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">
              {isVendor ? "Supplier Dashboard" : "Dashboard"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Welcome back, {user?.name}</p>
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowWidgetConfig(!showWidgetConfig)} data-testid="widget-config-btn">
              <GearSix size={14} className="mr-1" /> Customize
            </Button>
          )}
        </div>
      </div>

      {/* Widget Configuration Panel */}
      {showWidgetConfig && isAdmin && (
        <div className="border border-border rounded-md p-4 mb-6 bg-muted/30 animate-fade-in" data-testid="widget-config-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold font-[Chivo]">Dashboard Widgets</h3>
              <p className="text-[10px] text-muted-foreground mt-0.5">Toggle visibility and drag to reorder</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-xs" onClick={resetWidgets} data-testid="reset-widgets-btn">Reset</Button>
              <Button size="sm" className="text-xs" onClick={saveWidgets} disabled={savingWidgets} data-testid="save-widgets-btn">Save Layout</Button>
            </div>
          </div>
          <div className="space-y-1.5">
            {widgets.map((w, idx) => (
              <div key={w.id} className={`flex items-center justify-between py-2 px-3 rounded border transition-colors text-xs ${!w.visible ? "border-border/30 bg-muted/20 opacity-60" : "border-border/50 bg-card"}`} data-testid={`widget-row-${w.id}`}>
                <div className="flex items-center gap-2.5">
                  <ArrowsDownUp size={12} className="text-muted-foreground/40" />
                  <button onClick={() => toggleWidget(w.id)} className="text-muted-foreground hover:text-foreground" data-testid={`toggle-${w.id}`}>
                    {w.visible ? <Eye size={14} className="text-emerald-500" /> : <EyeSlash size={14} />}
                  </button>
                  <span className={`font-medium ${!w.visible ? "line-through text-muted-foreground" : ""}`}>{w.title}</span>
                </div>
                <div className="flex items-center gap-0.5">
                  <button onClick={() => moveWidget(idx, -1)} disabled={idx === 0} className="p-1 hover:bg-muted rounded disabled:opacity-30" data-testid={`move-up-${w.id}`}><CaretUp size={12} /></button>
                  <button onClick={() => moveWidget(idx, 1)} disabled={idx === widgets.length - 1} className="p-1 hover:bg-muted rounded disabled:opacity-30" data-testid={`move-down-${w.id}`}><CaretDown size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic Widget Rendering — all widgets render in config order */}
      {isAdmin && stats && (
        <>
          {widgets.filter(w => w.visible).sort((a, b) => a.order - b.order).map(w => {
            switch (w.type) {
              case "stats_overview":
                return (
                  <div key={w.id} data-testid="widget-stats-overview">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <StatCard icon={Hand} label="Unclaimed Requests" value={workload ? workload.unclaimed_requests : "—"} color="text-red-500" delay="animate-delay-1" href="/job-requests?team=unclaimed" accent="stat-accent-red" />
                      <div className="md:col-span-2 border border-border rounded-md p-4 bg-card animate-fade-in animate-delay-2" data-testid="workload-overview">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground">Team Workload</span>
                          <UsersThree size={18} className="text-primary" weight="duotone" />
                        </div>
                        {!workload || (workload.workload || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground italic">No claimed work yet — claim requests and jobs to see the split here.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {workload.workload.slice(0, 6).map(wl => {
                              const totalWl = wl.requests + wl.jobs;
                              const maxWl = Math.max(...workload.workload.map(x => x.requests + x.jobs), 1);
                              return (
                                <button type="button" key={wl.name} onClick={() => openWorkload(wl.name)}
                                        className="w-full flex items-center gap-2 rounded px-1 py-0.5 hover:bg-muted/60 transition-colors text-left"
                                        title={`See ${wl.name}'s requests and jobs`}
                                        data-testid={`workload-row-${wl.name.replace(/\s/g, "-").toLowerCase()}`}>
                                  <span className="text-xs font-medium w-24 truncate shrink-0">{wl.name}</span>
                                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                    <div className="h-full bg-primary rounded-full transition-[width]" style={{ width: `${(totalWl / maxWl) * 100}%` }} />
                                  </div>
                                  <span className="text-[10px] text-muted-foreground w-28 text-right shrink-0">{wl.requests} req · {wl.jobs} jobs</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <Dialog open={!!wlName} onOpenChange={(o) => { if (!o) setWlName(null); }}>
                      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="workload-dialog">
                        <DialogHeader>
                          <DialogTitle>{wlName}'s workload</DialogTitle>
                          <DialogDescription>Open requests and jobs currently claimed by {wlName}.</DialogDescription>
                        </DialogHeader>
                        {!wlItems ? <p className="text-sm text-muted-foreground">Loading…</p> : (
                          <div className="space-y-4">
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Requests ({wlItems.requests?.length || 0})</p>
                              {(wlItems.requests || []).length === 0 ? <p className="text-xs text-muted-foreground italic">None</p> : (
                                <div className="border border-border rounded-md divide-y divide-border">
                                  {wlItems.requests.map(r => (
                                    <Link key={r.id} to={`/job-requests/${r.id}`} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted" data-testid={`workload-req-${r.id}`}>
                                      <span className="text-sm truncate">{r.reference ? `${r.reference} — ` : ""}{r.title}</span>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">{r.status}</span>
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Jobs ({wlItems.jobs?.length || 0})</p>
                              {(wlItems.jobs || []).length === 0 ? <p className="text-xs text-muted-foreground italic">None</p> : (
                                <div className="border border-border rounded-md divide-y divide-border">
                                  {wlItems.jobs.map(j => (
                                    <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted" data-testid={`workload-job-${j.id}`}>
                                      <span className="text-sm truncate">{j.reference ? `${j.reference} — ` : ""}{j.title}</span>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">{j.status}</span>
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Link to={`/job-requests?team=${encodeURIComponent(wlName || "")}`} className="text-xs underline text-primary" data-testid="workload-open-requests">Open in Requests</Link>
                              <Link to={`/jobs?team=${encodeURIComponent(wlName || "")}`} className="text-xs underline text-primary" data-testid="workload-open-jobs">Open in Jobs</Link>
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <StatCard icon={Folder} label="Active Projects" value={stats.active_projects} color="text-blue-500" delay="animate-delay-1" href="/projects" accent="stat-accent-blue" />
                      <StatCard icon={ListChecks} label="Total Tasks" value={stats.total_tasks} color="text-emerald-500" delay="animate-delay-2" href="/projects" accent="stat-accent-emerald" />
                      <StatCard icon={Warning} label="Overdue Tasks" value={stats.overdue_tasks} color="text-red-500" delay="animate-delay-3" href="/projects" accent="stat-accent-red" />
                      <StatCard icon={Clock} label="Pending Review" value={stats.pending_review} color="text-amber-500" delay="animate-delay-4" href="/projects" accent="stat-accent-amber" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <StatCard icon={Truck} label="Active Suppliers" value={stats.total_vendors} color="text-purple-500" delay="animate-delay-5" href="/vendors" accent="stat-accent-purple" />
                      <StatCard icon={ClipboardText} label="Works Orders" value={stats.total_work_orders} color="text-sky-500" delay="animate-delay-6" href="/work-orders" accent="stat-accent-sky" />
                      <StatCard icon={CurrencyDollar} label="Total Budget" value={`£${(stats.total_budget || 0).toLocaleString()}`} color="text-green-500" delay="animate-delay-7" href="/reports" accent="stat-accent-green" />
                      <StatCard icon={Folder} label="All Projects" value={stats.total_projects} color="text-indigo-500" delay="animate-delay-8" href="/projects" accent="stat-accent-indigo" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <StatCard icon={ClipboardText} label="Active Requests" value={stats.jr_active || 0} color="text-blue-500" delay="animate-delay-1" href="/job-requests" accent="stat-accent-blue" />
                      <StatCard icon={ClipboardText} label="Total Requests" value={stats.jr_total || 0} color="text-slate-500" delay="animate-delay-2" href="/job-requests" accent="stat-accent-slate" />
                      <StatCard icon={Warning} label="Overdue Requests" value={stats.jr_overdue || 0} color="text-red-500" delay="animate-delay-3" href="/job-requests" accent="stat-accent-red" />
                      <StatCard icon={Clock} label="Pending Approval" value={stats.jr_pending || 0} color="text-amber-500" delay="animate-delay-4" href="/job-requests" accent="stat-accent-amber" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <StatCard icon={Receipt} label="Active Quotes" value={stats.qt_active || 0} color="text-teal-500" delay="animate-delay-1" href="/quotes" accent="stat-accent-teal" />
                      <StatCard icon={Receipt} label="Total Quotes" value={stats.qt_total || 0} color="text-slate-500" delay="animate-delay-2" href="/quotes" accent="stat-accent-slate" />
                      <StatCard icon={Receipt} label="Sent Quotes" value={stats.qt_sent || 0} color="text-blue-500" delay="animate-delay-3" href="/quotes" accent="stat-accent-blue" />
                      <StatCard icon={Receipt} label="Approved Quotes" value={stats.qt_approved || 0} color="text-emerald-500" delay="animate-delay-4" href="/quotes" accent="stat-accent-emerald" />
                    </div>
                    {/* SLA + Breach widgets — relocated here so they sit above Supplier Compliance */}
                    <SLAResponseWidget />
                    <BreachInsightsWidget />
                  </div>
                );
              case "compliance_status":
                return complianceSummary && (complianceSummary.expired > 0 || complianceSummary.expiring_soon > 0 || complianceSummary.missing_files > 0) ? (
                  <div key={w.id} className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-md p-4 mb-6 animate-fade-in" data-testid="compliance-alert-admin">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <ShieldWarning size={18} className="text-amber-600 dark:text-amber-400" />
                        <div>
                          <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Supplier Compliance</h3>
                          <p className="text-xs text-amber-700 dark:text-amber-400">
                            {complianceSummary.expired > 0 && `${complianceSummary.expired} expired`}
                            {complianceSummary.expired > 0 && complianceSummary.expiring_soon > 0 && ", "}
                            {complianceSummary.expiring_soon > 0 && `${complianceSummary.expiring_soon} expiring soon`}
                            {(complianceSummary.expired > 0 || complianceSummary.expiring_soon > 0) && complianceSummary.missing_files > 0 && ", "}
                            {complianceSummary.missing_files > 0 && (
                              <span data-testid="compliance-admin-missing-files">{`${complianceSummary.missing_files} missing file upload`}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {complianceSummary.missing_files > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            data-testid="email-missing-file-suppliers-admin-btn"
                            onClick={async () => {
                              try {
                                const { data } = await API.post("/compliance/email-missing-file-suppliers");
                                toast.success(data.message || `Emailed ${data.sent} supplier(s).`);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Failed to email suppliers.");
                              }
                            }}
                          >
                            <EnvelopeSimple size={14} className="mr-1" /> Email Suppliers
                          </Button>
                        )}
                        {complianceSummary.orphan_docs > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            data-testid="cleanup-orphan-docs-btn"
                            onClick={async () => {
                              if (!window.confirm(`Permanently delete ${complianceSummary.orphan_docs} compliance record(s) whose supplier no longer exists? This cannot be undone.`)) return;
                              try {
                                const { data } = await API.post("/compliance/cleanup-orphan-docs");
                                toast.success(data.message || `Deleted ${data.deleted} record(s).`);
                                const refresh = await API.get("/compliance/summary");
                                setComplianceSummary(refresh.data);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Cleanup failed.");
                              }
                            }}
                          >
                            <Trash size={14} className="mr-1" /> Cleanup {complianceSummary.orphan_docs} Orphan{complianceSummary.orphan_docs === 1 ? "" : "s"}
                          </Button>
                        )}
                        <Link to="/vendors"><Button variant="outline" size="sm" className="text-xs" data-testid="go-to-compliance"><ShieldCheck size={14} className="mr-1" /> View <ArrowRight size={12} className="ml-1" /></Button></Link>
                      </div>
                    </div>
                  </div>
                ) : <div key={w.id} />;
              case "recent_activity":
                return (
                  <div key={w.id} className="border border-border rounded-md animate-fade-in mb-6" data-testid="widget-recent-projects">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                      <h3 className="text-sm font-semibold font-[Chivo] section-header-line pb-3 mb-3">Recent Projects</h3>
                      <Link to="/projects"><Button variant="ghost" size="sm" className="text-xs" data-testid="view-all-projects">View All</Button></Link>
                    </div>
                    <div className="divide-y divide-border">
                      {(stats.recent_projects || []).slice(0, 5).map((p) => (
                        <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center justify-between px-4 py-3 row-hover transition-colors">
                          <div><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-muted-foreground">{p.reference}</p></div>
                          <StatusBadge status={p.status} />
                        </Link>
                      ))}
                      {(!stats.recent_projects || stats.recent_projects.length === 0) && (
                        <p className="px-4 py-6 text-sm text-muted-foreground text-center">No projects yet</p>
                      )}
                    </div>
                  </div>
                );
              case "task_chart":
                return (
                  <div key={w.id} className="border border-border rounded-md animate-fade-in animate-delay-2 mb-6" data-testid="widget-recent-tasks">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                      <h3 className="text-sm font-semibold font-[Chivo] section-header-line pb-3 mb-3">Recent Tasks</h3>
                    </div>
                    <div className="divide-y divide-border">
                      {(stats.recent_tasks || []).slice(0, 5).map((t) => (
                        <Link key={t.id} to={`/projects/${t.project_id}/tasks/${t.id}`} className="flex items-center justify-between px-4 py-3 row-hover transition-colors">
                          <div><p className="text-sm font-medium">{t.title}</p><p className="text-xs text-muted-foreground">{t.project_name}</p></div>
                          <StatusBadge status={t.status} />
                        </Link>
                      ))}
                      {(!stats.recent_tasks || stats.recent_tasks.length === 0) && (
                        <p className="px-4 py-6 text-sm text-muted-foreground text-center">No tasks yet</p>
                      )}
                    </div>
                  </div>
                );
              case "scheduler_health":
                return <div key={w.id} className="mb-6"><SchedulerWidget /></div>;
              case "schedule_calendar":
                return <div key={w.id} className="mb-6" data-testid="widget-calendar"><DashboardCalendar /></div>;
              case "analytics_charts":
                return <div key={w.id} className="mb-6" data-testid="widget-analytics"><AnalyticsWidgets /></div>;
              case "emergency_overview":
                return <div key={w.id} className="mb-6" data-testid="widget-emergency"><EmergencyJobsWidget /></div>;
              case "activity_feed":
                return <div key={w.id} className="mb-6" data-testid="widget-activity"><ActivityFeedWidget /></div>;
              case "in_house_live_team":
                return <div key={w.id} className="mb-6"><InHouseLiveTeamWidget /></div>;
              case "in_house_expenses_pending":
                return <div key={w.id} className="mb-6"><InHouseExpensesPendingWidget /></div>;
              case "in_house_pos_pending":
                return <div key={w.id} className="mb-6"><InHousePosPendingWidget /></div>;
              case "tomorrow_jobs_at_risk":
                return <div key={w.id} className="mb-6"><TomorrowJobsAtRiskWidget /></div>;
              case "unassigned_this_week":
                return <div key={w.id} className="mb-6"><UnassignedThisWeekWidget /></div>;
              default:
                return null;
            }
          })}
        </>
      )}

      {isVendor && stats && (
        <>
          {/* At-a-glance stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard icon={Lightning} label="Active Emergencies" value={stats.active_emergency_jobs_count || 0} color="text-red-500" delay="animate-delay-1" href="/emergency-jobs" accent="stat-accent-red" />
            <StatCard icon={Briefcase} label="Active Jobs" value={stats.active_jobs_count || 0} color="text-blue-500" delay="animate-delay-2" href="/jobs" accent="stat-accent-blue" />
            <StatCard icon={ListChecks} label="Assigned Tasks" value={stats.assigned_tasks} color="text-emerald-500" delay="animate-delay-3" href="/projects" accent="stat-accent-emerald" />
            <StatCard icon={ClipboardText} label="Work Orders" value={(stats.my_work_orders || []).length} color="text-violet-500" delay="animate-delay-4" href="/work-orders" accent="stat-accent-purple" />
          </div>

          {/* 1. Assigned Emergency Jobs */}
          <VendorBookingWidget
            title="Assigned Emergency Jobs"
            accentColor="red"
            icon={Lightning}
            items={stats.my_emergency_jobs || []}
            viewAllHref="/emergency-jobs"
            getItemHref={(ej) => `/emergency-jobs/${ej.id}`}
            pulse={pulseKey === "emergencies" ? pulseNonce : 0}
            data-testid="vendor-emergency-widget"
            renderPrimary={(ej) => { const sla = emergencySla(ej); return <span className="inline-flex items-center gap-1.5" data-sla-tier={sla.tier}><SlaLadderBeacon sla={sla} size="h-2 w-2" />{ej.title || "Emergency Job"}<SlaTierBadge sla={sla} testId={`vendor-emg-sla-${ej.id}`} /></span>; }}
            renderMeta={(ej) => {
              const parts = [];
              if (ej.urgency) parts.push(`Urgency: ${ej.urgency.toUpperCase()}`);
              if (ej.reference) parts.push(ej.reference);
              if (ej.response_hours) parts.push(`${ej.response_hours}h SLA`);
              else if (ej.sla_minutes) parts.push(`SLA ${ej.sla_minutes}m`);
              return parts.join(" · ");
            }}
            emptyLabel="No active emergency jobs"
          />

          {/* 2. Assigned Jobs */}
          <VendorBookingWidget
            title="Assigned Jobs"
            accentColor="blue"
            icon={Briefcase}
            items={stats.my_jobs || []}
            viewAllHref="/jobs"
            getItemHref={(j) => `/jobs/${j.id}`}
            pulse={pulseKey === "jobs" ? pulseNonce : 0}
            data-testid="vendor-jobs-widget"
            renderPrimary={(j) => j.title || j.reference || "Job"}
            renderMeta={(j) => {
              const parts = [];
              if (j.reference) parts.push(j.reference);
              if (j.schedule?.start_date) parts.push(`Due: ${ukDate(j.schedule.start_date)}`);
              if (j.category) parts.push(j.category);
              return parts.join(" · ");
            }}
            emptyLabel="No assigned jobs"
          />

          {/* 3. Assigned Tasks */}
          <VendorBookingWidget
            title="Assigned Tasks"
            accentColor="emerald"
            icon={ListChecks}
            items={stats.my_tasks || []}
            viewAllHref="/projects"
            getItemHref={(t) => `/projects/${t.project_id}/tasks/${t.id}`}
            pulse={pulseKey === "tasks" ? pulseNonce : 0}
            data-testid="vendor-tasks-widget"
            renderPrimary={(t) => t.title || "Task"}
            renderMeta={(t) => {
              const parts = [];
              if (t.project_name) parts.push(t.project_name);
              if (t.due_date) parts.push(`Due: ${ukDate(t.due_date)}`);
              return parts.join(" · ");
            }}
            renderActions={(t) => (
              <>
                <EnRouteButton id={t.id} enRouteAt={t.en_route_at} arrivedAt={t.arrived_at} compact data-testid={`task-en-route-${t.id}`} />
                <MarkAsArrivedButton type="task" id={t.id} arrivedAt={t.arrived_at} compact data-testid={`task-arrived-${t.id}`} />
                <GetDirectionsButton address={t.property_address} latitude={t.latitude} longitude={t.longitude} variant="outline" size="sm" iconOnly data-testid={`task-directions-${t.id}`} />
              </>
            )}
            emptyLabel="No assigned tasks"
          />

          {/* 4. Work Orders */}
          <VendorBookingWidget
            title="Work Orders"
            accentColor="violet"
            icon={ClipboardText}
            items={stats.my_work_orders || []}
            viewAllHref="/work-orders"
            getItemHref={() => "/work-orders"}
            pulse={pulseKey === "work_orders" ? pulseNonce : 0}
            data-testid="vendor-work-orders-widget"
            renderPrimary={(wo) => wo.wo_number || "Work Order"}
            renderMeta={(wo) => {
              const parts = [];
              if (wo.project_name) parts.push(wo.project_name);
              if (wo.total_budget != null) parts.push(`£${(wo.total_budget || 0).toLocaleString()}`);
              if (wo.due_date) parts.push(`Due: ${ukDate(wo.due_date)}`);
              return parts.join(" · ");
            }}
            renderActions={(wo) => (
              <>
                <MarkAsArrivedButton type="wo" id={wo.id} arrivedAt={wo.arrived_at} compact data-testid={`wo-arrived-${wo.id}`} />
                <GetDirectionsButton address={wo.property_address} latitude={wo.latitude} longitude={wo.longitude} variant="outline" size="sm" iconOnly data-testid={`wo-directions-${wo.id}`} />
                {wo.revision_number > 0 && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">R{wo.revision_number}</span>}
              </>
            )}
            emptyLabel="No active work orders"
          />
        </>
      )}
    </div>
  );
}


function VendorBookingWidget({ title, accentColor = "blue", icon: Icon, items, viewAllHref, getItemHref, renderPrimary, renderMeta, renderActions, emptyLabel, pulse = 0, ...rest }) {
  const accentMap = {
    red: { chip: "text-red-500 bg-red-500/10", pulse: "widget-pulse-red", flash: "rgba(239,68,68,0.18)" },
    blue: { chip: "text-blue-500 bg-blue-500/10", pulse: "widget-pulse-blue", flash: "rgba(59,130,246,0.18)" },
    emerald: { chip: "text-emerald-500 bg-emerald-500/10", pulse: "widget-pulse-emerald", flash: "rgba(16,185,129,0.18)" },
    violet: { chip: "text-violet-500 bg-violet-500/10", pulse: "widget-pulse-violet", flash: "rgba(139,92,246,0.18)" },
  };
  const accent = accentMap[accentColor] || accentMap.blue;
  const list = items || [];
  const testid = rest["data-testid"];

  const [active, setActive] = useState(false);
  useEffect(() => {
    if (pulse > 0) {
      setActive(true);
      const timer = setTimeout(() => setActive(false), 3300);
      return () => clearTimeout(timer);
    }
  }, [pulse]);

  return (
    <div
      className={`border border-border rounded-md animate-fade-in mb-6 bg-card transition-colors ${active ? `widget-pulse ${accent.pulse}` : ""}`}
      data-testid={testid}
      data-pulsing={active ? "true" : "false"}
    >
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`h-7 w-7 rounded-md flex items-center justify-center ${accent.chip}`}>
            <Icon size={14} weight="duotone" />
          </div>
          <h3 className="text-sm font-semibold font-[Chivo]">{title}</h3>
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded transition-colors ${active ? `${accent.chip} font-semibold` : "text-muted-foreground bg-muted/60"}`} data-testid={`${testid}-count`}>
            {list.length}
          </span>
          {active && (
            <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1" data-testid={`${testid}-live-badge`}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              New
            </span>
          )}
        </div>
        <Link to={viewAllHref} className="text-xs text-primary hover:underline" data-testid={`${testid}-view-all`}>View all</Link>
      </div>
      <div className="divide-y divide-border">
        {list.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground text-center">{emptyLabel}</p>
        ) : (
          list.map((it, idx) => {
            const href = getItemHref(it);
            const address = it.property_address || it.street_address || "";
            const flash = active && idx === 0;
            return (
              <div
                key={it.id || it.wo_number}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${flash ? "row-flash" : ""}`}
                style={flash ? { "--flash-color": accent.flash } : undefined}
                data-testid={`${testid}-row-${it.id}`}
              >
                <Link to={href} className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{renderPrimary(it)}</p>
                  {renderMeta && <p className="text-xs text-muted-foreground truncate">{renderMeta(it)}</p>}
                  {address && (
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                      <MapPin size={10} /> {address}
                    </p>
                  )}
                </Link>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  {renderActions && renderActions(it)}
                  <StatusBadge status={it.status} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}




function EmergencyJobsWidget() {
  const [jobs, setJobs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    API.get("/emergency-jobs?limit=50").then(({ data }) => setJobs(data.jobs || [])).catch(() => {});
    API.get("/emergency-jobs/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    API.get("/emergency-jobs/supplier-response").then(({ data }) => setSupplierData(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const activeJobs = jobs.filter(j => !["completed", "cancelled"].includes(j.status));
  if (activeJobs.length === 0 && (!analytics || analytics.total_jobs === 0)) return null;

  const urgencyColors = { critical: "#dc2626", urgent: "#ea580c", moderate: "#d97706", minor: "#2563eb" };
  const urgencyBg = { critical: "bg-red-600", urgent: "bg-orange-500", moderate: "bg-amber-500", minor: "bg-blue-500" };
  const urgencyLabels = { critical: "Critical", urgent: "Urgent", moderate: "Moderate", minor: "Minor" };
  const statusBg = {
    open: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    acknowledged: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  };

  const criticalActive = activeJobs.filter(j => j.urgency === "critical").length;
  const overdueJobs = activeJobs.filter(j => emergencySla(j).tier === "breached");
  const openCount = activeJobs.filter(j => j.status === "open").length;
  const ackedCount = activeJobs.filter(j => j.status === "acknowledged").length;

  // SLA compliance from analytics
  const slaRate = analytics?.sla_compliance
    ? (() => {
        const entries = Object.values(analytics.sla_compliance).filter(s => s.total > 0);
        if (!entries.length) return null;
        return Math.round(entries.reduce((sum, s) => sum + s.rate, 0) / entries.length);
      })()
    : null;

  // Best/fastest supplier
  const fastest = supplierData?.overall?.fastest_supplier;
  const fastestHours = supplierData?.overall?.fastest_avg_hours;

  // Urgency distribution for mini chart
  const urgencyDist = ["critical", "urgent", "moderate", "minor"].map(u => ({
    key: u, label: urgencyLabels[u], color: urgencyColors[u],
    active: activeJobs.filter(j => j.urgency === u).length,
    total: (analytics?.by_urgency?.[u]?.total) || 0,
  }));
  const maxUrgTotal = Math.max(1, ...urgencyDist.map(d => d.total));


  return (
    <div className="border border-red-200 dark:border-red-800/40 rounded-lg overflow-hidden bg-card" data-testid="emergency-jobs-widget">
      {/* Header */}
      <div className="px-4 py-3 bg-red-50 dark:bg-red-950/20 border-b border-red-200 dark:border-red-800/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightning size={16} className="text-red-600" weight="fill" />
          <span className="text-sm font-bold font-[Chivo]">Emergency Overview</span>
          {activeJobs.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-600 text-white font-bold">{activeJobs.length} active</span>}
          {overdueJobs.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-800 text-white font-bold animate-pulse">{overdueJobs.length} overdue</span>}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/emergency-jobs/analytics" className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5" data-testid="emg-widget-analytics-link"><ChartBar size={10} /> Analytics</Link>
          <Link to="/emergency-jobs" className="text-xs text-red-600 hover:underline flex items-center gap-1" data-testid="emg-widget-view-all">View all <ArrowRight size={10} /></Link>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-0 border-b border-red-100 dark:border-red-900/30" data-testid="emg-widget-kpis">
        <div className="px-3 py-2.5 border-r border-red-100 dark:border-red-900/30" data-testid="emg-widget-kpi-active">
          <p className="text-lg font-bold font-[Chivo] text-red-600">{activeJobs.length}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Active</p>
        </div>
        <div className="px-3 py-2.5 border-r border-red-100 dark:border-red-900/30" data-testid="emg-widget-kpi-critical">
          <p className={`text-lg font-bold font-[Chivo] ${criticalActive > 0 ? "text-red-600" : "text-muted-foreground"}`}>{criticalActive}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Critical</p>
        </div>
        <div className="px-3 py-2.5 border-r border-red-100 dark:border-red-900/30" data-testid="emg-widget-kpi-open">
          <div className="flex items-baseline gap-1.5">
            <p className="text-lg font-bold font-[Chivo]">{openCount}</p>
            <p className="text-[10px] text-muted-foreground">/ {ackedCount} ack</p>
          </div>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Open</p>
        </div>
        <div className="px-3 py-2.5" data-testid="emg-widget-kpi-sla">
          <p className={`text-lg font-bold font-[Chivo] ${slaRate != null ? (slaRate >= 80 ? "text-emerald-600" : slaRate >= 50 ? "text-amber-600" : "text-red-600") : "text-muted-foreground"}`}>
            {slaRate != null ? `${slaRate}%` : "—"}
          </p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">SLA Rate</p>
        </div>
      </div>

      {/* Urgency Distribution Bars */}
      <div className="px-4 py-3 border-b border-red-100 dark:border-red-900/30" data-testid="emg-widget-urgency-dist">
        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Urgency Distribution</p>
        <div className="space-y-1.5">
          {urgencyDist.map(d => (
            <div key={d.key} className="flex items-center gap-2">
              <span className="text-[9px] font-bold w-14 shrink-0" style={{ color: d.color }}>{d.label}</span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(d.total / maxUrgTotal) * 100}%`, backgroundColor: d.color }} />
              </div>
              <span className="text-[9px] font-mono text-muted-foreground w-8 text-right">{d.total}</span>
              {d.active > 0 && <span className="text-[9px] font-bold px-1 py-0.5 rounded text-white" style={{ backgroundColor: d.color }}>{d.active}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Fastest Supplier */}
      {fastest && (
        <div className="px-4 py-2.5 border-b border-red-100 dark:border-red-900/30 flex items-center justify-between" data-testid="emg-widget-fastest-supplier">
          <div className="flex items-center gap-2">
            <Star size={12} className="text-amber-500" weight="fill" />
            <span className="text-[10px] text-muted-foreground">Fastest Responder:</span>
            <span className="text-[10px] font-semibold">{fastest}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {fastestHours != null && (
              <span className="text-[10px] font-bold font-mono text-emerald-600">
                {fastestHours < 1 ? `${Math.round(fastestHours * 60)}m` : `${fastestHours}h`} avg
              </span>
            )}
            <Link to="/emergency-jobs/supplier-response" className="text-[9px] text-primary hover:underline" data-testid="emg-widget-supplier-link">Details</Link>
          </div>
        </div>
      )}

      {/* Active Jobs List */}
      {activeJobs.length > 0 && (
        <div className="divide-y divide-border" data-testid="emg-widget-jobs-list">
          {activeJobs.slice(0, 5).map(j => {
            const sla = emergencySla(j);
            const stBg = statusBg[j.status] || "";

            return (
              <Link key={j.id} to={`/emergency-jobs/${j.id}`} className={`flex items-center justify-between px-4 py-2.5 hover:bg-muted/20 transition-colors border-l-4 ${sla.ui.edge} ${sla.tier === "breached" ? "bg-red-50/70 dark:bg-red-950/30" : sla.tier === "red" ? "bg-red-50/40 dark:bg-red-950/15" : sla.tier === "amber" ? "bg-amber-50/50 dark:bg-amber-950/15" : ""}`} data-testid={`emg-widget-${j.id}`} data-sla-tier={sla.tier}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                    <SlaLadderBeacon sla={sla} size="h-2 w-2" />
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold text-white ${urgencyBg[j.urgency] || "bg-orange-500"}`}>{urgencyLabels[j.urgency] || "Urgent"}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{j.reference}</span>
                    {stBg && <span className={`text-[8px] px-1 py-0.5 rounded font-medium ${stBg}`}>{j.status === "acknowledged" ? "Ack'd" : j.status === "in_progress" ? "In Prog" : "Open"}</span>}
                    <SlaTierBadge sla={sla} testId={`emg-widget-sla-${j.id}`} />
                  </div>
                  <p className="text-xs font-medium truncate">{j.title}</p>
                  {j.assigned_vendor_name && <p className="text-[9px] text-muted-foreground flex items-center gap-0.5 mt-0.5"><Wrench size={8} />{j.assigned_vendor_name}</p>}
                </div>
                <div className="shrink-0 ml-3 min-w-[90px]">
                  <SlaTimer sla={sla} compact status={j.status} testId={`emg-widget-timer-${j.id}`} />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {activeJobs.length > 5 && (
        <div className="px-4 py-2 bg-muted/20 border-t border-red-100 dark:border-red-900/30 text-center">
          <Link to="/emergency-jobs" className="text-[10px] text-red-600 hover:underline font-medium">+{activeJobs.length - 5} more active emergencies</Link>
        </div>
      )}
    </div>
  );
}



function ActivityFeedWidget() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/activity-log?limit=15").then(({ data }) => setActivities(data.logs || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !activities.length) return null;

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const getIcon = (action) => {
    if (action?.includes("create") || action?.includes("add")) return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600";
    if (action?.includes("update") || action?.includes("edit")) return "bg-blue-100 dark:bg-blue-900/30 text-blue-600";
    if (action?.includes("delete") || action?.includes("remove")) return "bg-red-100 dark:bg-red-900/30 text-red-600";
    if (action?.includes("complete") || action?.includes("approve")) return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600";
    return "bg-muted text-muted-foreground";
  };

  return (
    <div className="mt-6 animate-fade-in" data-testid="dashboard-activity-feed">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold font-[Chivo] section-header-line">Activity Feed</h2>
        <Link to="/settings" className="text-xs text-primary hover:underline">View All</Link>
      </div>
      <div className="border border-border rounded-md divide-y divide-border bg-card" data-testid="activity-feed-list">
        {activities.map((a, i) => (
          <div key={a.id || i} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/10 transition-colors" data-testid={`activity-${i}`}>
            <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${getIcon(a.action)}`}>
              <Pulse size={12} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs"><span className="font-semibold">{a.user_name}</span> <span className="text-muted-foreground">{a.description || a.action}</span></p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{a.entity_type && <span className="capitalize">{a.entity_type.replace(/_/g, " ")} </span>}{timeAgo(a.created_at)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
